import { useState, useRef, useEffect } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/partnerProgramPage.module.scss';
import tableStyles from '../css/table.module.scss';
import { Link } from 'react-router-dom';
import Pagination from '../components/pagination';
import { createPortal } from 'react-dom';
import CustomDropdown from '../components/customDropdown';
import CustomSearchInput from '../components/customSearchInput';
import { useClickAway } from 'react-use';
import { formatStatus } from '../helpers/functions';
import CustomCalendar from '../components/customCalendar';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

interface analyticsData {
  data: [
    {
      _id: string;
      partnerName: string;
      partnerEmail: string;
      referralLink: string;
      planPurchases: number;
      earnings: number;
      isBlocked: boolean;
      partnerOrganizationId: string;
      permanentlyRemoved?: boolean;
    },
  ];
  pagination: {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    totalPages: number;
  };
}

interface linksData {
  data: [
    {
      _id: string;
      referralLink: string;
      status: string;
      dateCreated: string;
      isBlocked: boolean;
      partnerOrganizationId: string;
      permanentlyRemoved?: boolean;
    },
  ];
  pagination: {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    totalPages: number;
  };
}

interface earningsData {
  data: [
    {
      partnerId: string;
      email: string;
      rewardAmount: number;
      status: string;
      dateAdded: string;
      expiryDate: string;
      isBlocked: boolean;
      partnerOrganizationId: string;
      permanentlyRemoved?: boolean;
    },
  ];
  pagination: {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    totalPages: number;
  };
}

interface fetchedData {
  analytics: analyticsData;
  referralLinks: linksData;
  earnings: earningsData;
  referralLinkVisits: number;
}

const PartnerProgram = () => {
  const { user } = useAuth();
  const [view, setView] = useState<'analytics' | 'links' | 'earnings'>('analytics');
  const [isActionOpen, setIsActionOpen] = useState<number | string | null>(null);
  const actionDropdownRef = useRef<HTMLDivElement>(null);

  const [currentPageAnalytics, setCurrentPageAnalytics] = useState(1);
  const [currentPageLinks, setCurrentPageLinks] = useState(1);
  const [currentPageEarnings, setCurrentPageEarnings] = useState(1);
  const [analyticsSearchTerm, setAnalyticsSearchTerm] = useState<string>('');
  const [linksSearchTerm, setLinksSearchTerm] = useState<string>('');
  const [earningsSearchTerm, setEarningsSearchTerm] = useState<string>('');

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dateValues, setDateValues] = useState<any[]>([null, null]);
  const { t } = useTranslation();
  const [status, setStatus] = useState<{ value: string; label: string }>({ value: 'all', label: t('partnerProgram.labels.all') });
  const [filterStatusOpen, setFilterStatusOpen] = useState<boolean>(false);
  const [sortByEarnings, setSortByEarnings] = useState<{ value: string; label: string }>({ value: 'desc', label: t('partnerProgram.labels.descending') });
  const [filterSortByEarningsOpen, setFilterSortByEarningsOpen] = useState<boolean>(false);
  const [filterSortByUserOpen, setFilterSortByUserOpen] = useState<boolean>(false);
  const filterStatusDropRef = useRef<HTMLDivElement>(null);
  const filterSortByEarningsDropRef = useRef<HTMLDivElement>(null);
  const filterSortByUserDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dateRangeRef = useRef(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchedData, setFetchedData] = useState<fetchedData | null>(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      axios
        .get(
          `${EnvPaths.SERVER_URL}/admin/get-all-partner-insights?analyticsPage=${currentPageAnalytics ?? 1}&linksPage=${currentPageLinks ?? 1}&earningsPage=${currentPageEarnings ?? 1}&analyticsQ=${analyticsSearchTerm ?? ''}&linksQ=${linksSearchTerm ?? ''}&earningsQ=${earningsSearchTerm ?? ''}&analyticsSortByEarnings=${sortByEarnings.value}&analyticsDateFrom=${formatDateForBackend(dateValues[0]) ?? ''}&analyticsDateTo=${formatDateForBackend(dateValues[1]) ?? ''}&earningsSortByEarnings=${sortByEarnings.value}&earningsSortByStatus=${status.value}`,
          {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          }
        )
        .then((response) => {
          setFetchedData(response.data);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsActionOpen(null);
          setIsLoading(false);
        });
    } catch (error) {
      console.error(error);
    }
  };

  const handleActionClick = (id: number | string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  useClickAway(filterStatusDropRef, () => {
    setFilterStatusOpen(false);
  });

  const filterStatusOptions = [
    { value: 'all', label: t('partnerProgram.labels.all') },
    { value: 'approved', label: t('partnerProgram.labels.approved') },
    { value: 'expired', label: t('partnerProgram.labels.expired') },
  ];

  const handleStatusChange = (item: { value: string; label: string }) => {
    setStatus(item);
    setFilterStatusOpen(false);
  };

  useClickAway(filterSortByUserDropRef, () => {
    setFilterSortByUserOpen(false);
  });

  useClickAway(filterSortByEarningsDropRef, () => {
    setFilterSortByEarningsOpen(false);
  });

  const filterSortByEarningsOptions = [
    { value: 'asc', label: t('partnerProgram.labels.ascending') },
    { value: 'desc', label: t('partnerProgram.labels.descending') },
  ];

  const handleSortByEarningsChange = (item: { value: string; label: string }) => {
    setSortByEarnings(item);
    setFilterSortByEarningsOpen(false);
  };

  const handleDateChange = (value: any) => {
    setDateValues(value);
  };

  const handleDateOnCancel = () => {
    setDateValues([null, null]);
  };

  const formatDateForBackend = (date: any) => {
    if (date && date.toDate) {
      return date?.toDate()?.toISOString()?.split('T')[0];
    }
    return '';
  };

  useEffect(() => {
    fetchData();
  }, [user, currentPageAnalytics, currentPageLinks, currentPageEarnings, sortByEarnings, dateValues, analyticsSearchTerm, linksSearchTerm, status, earningsSearchTerm]);

  useEffect(() => {
    setStatus((prev) => ({ ...prev, label: t('partnerProgram.labels.all') }));
    setSortByEarnings((prev) => ({ ...prev, label: t('partnerProgram.labels.descending') }));
  }, [t, i18next]);

  const handleCSVExport = async () => {
    try {
      const response = await fetch(`${EnvPaths.SERVER_URL}/admin/download-partner-analytics`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `partner_analytics.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    try {
      await axios
        .get(`${EnvPaths.SERVER_URL}/admin/deactivate-user/${userId}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.error('Deactivation failed:', error);
        });
    } catch (error) {
      console.error('Deactivation failed:', error);
    }
  };

  const handleCopyToClipboard = async (link: string) => {
    try {
      const referralLink = EnvPaths.SITE_URL + '/explore-plans?referral=' + link;
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success(t('partnerProgram.handleCopyToClipboard.success'));
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error(t('partnerProgram.handleCopyToClipboard.error'));
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm(t('partnerProgram.handleDeleteUser'))) return;
    try {
      await axios
        .delete(`${EnvPaths.SERVER_URL}/admin/delete-user/${userId}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.error('Deletion failed:', error);
        });
    } catch (error) {
      console.error('Deletion failed:', error);
    }
  };

  useClickAway(actionDropdownRef, () => {
    setIsActionOpen(null);
  });

  return (
    <main className={styles.pageContainer}>
      <div className={globalPageStyle.pageFlexContainer} style={{ height: '100%' }}>
        {/*header - tabs*/}
        <div className={globalPageStyle.tabs}>
          <div className={`${globalPageStyle.tab} ${view === 'analytics' ? globalPageStyle.active : ''}`} onClick={() => setView('analytics')}>
            {t('partnerProgram.links.analytics')}
          </div>
          <div className={`${globalPageStyle.tab} ${view === 'links' ? globalPageStyle.active : ''}`} onClick={() => setView('links')}>
            {t('partnerProgram.links.links')}
          </div>
          <div className={`${globalPageStyle.tab} ${view === 'earnings' ? globalPageStyle.active : ''}`} onClick={() => setView('earnings')}>
            {t('partnerProgram.links.tracking')}
          </div>
        </div>
        {/*header - tabs*/}
        {/*body - page content*/}

        {user && user.role === 'admin' ? (
          isLoading ? (
            <div className="loader" />
          ) : (
            <div style={{ overflow: 'auto' }}>
              <div className={styles.pageContent}>
                {/*view analytics*/}
                {view === 'analytics' && (
                  <>
                    {/*filters*/}
                    <div className={styles.header}>
                      <div className={styles.headerLeft}>
                        <div className={styles.flexRow}>
                          <CustomSearchInput defaultValue={analyticsSearchTerm} onSearch={setAnalyticsSearchTerm} icon="/assets/images/search-icon.svg" placeholder={t('partnerProgram.search')} />
                          {/*filter*/}
                          <CustomDropdown
                            ref={filterSortByEarningsDropRef}
                            isOpen={filterSortByEarningsOpen}
                            onToggle={() => setFilterSortByEarningsOpen(!filterSortByEarningsOpen)}
                            options={filterSortByEarningsOptions}
                            value={sortByEarnings}
                            onChange={handleSortByEarningsChange}
                            width="255px"
                            label={t('partnerProgram.sortByEarnings')}
                          />
                          {/*date range - calendar*/}
                          {/*date range - calendar*/}
                          <CustomCalendar dateValues={dateValues} handleDateChange={handleDateChange} onCancel={handleDateOnCancel} ref={dateRangeRef} />
                          {/*date range - calendar*/}
                          {/*date range - calendar*/}
                          {/*filter*/}
                        </div>
                      </div>
                      <button type="button" className="primaryButton inverse" onClick={handleCSVExport}>
                        {t('partnerProgram.exportCsv')}{' '}
                      </button>
                    </div>
                    {/*filters*/}
                    {/*table*/}
                    <div className={styles.tableBody}>
                      <div className={tableStyles.tableContainer}>
                        <table className={`${tableStyles.table}`}>
                          <thead className={tableStyles.tableHeader}>
                            <tr>
                              <th>{t('partnerProgram.partnerName')}</th>
                              <th>{t('partnerProgram.email')}</th>
                              <th>{t('partnerProgram.planPurchases')}</th>
                              <th>{t('partnerProgram.earnings')}</th>
                              <th className={tableStyles.actionColumn}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fetchedData &&
                              fetchedData?.analytics?.data?.map((item, index) => {
                                return (
                                  <tr key={item._id}>
                                    <td>{item.partnerName}</td>
                                    <td>{item.partnerEmail}</td>
                                    <td>{item.planPurchases}</td>
                                    <td>€{item.earnings}</td>
                                    <td className={tableStyles.actionColumn}>
                                      <div className={tableStyles.actionShape} ref={buttonRef}>
                                        <img
                                          src="/assets/images/dots.svg"
                                          alt="dots"
                                          onClick={(e) => {
                                            const buttonElement = e.currentTarget as HTMLDivElement;
                                            handleActionClick(item._id, buttonElement);
                                          }}
                                        />
                                        {isActionOpen !== null &&
                                          isActionOpen === item._id &&
                                          typeof window !== 'undefined' &&
                                          createPortal(
                                            <div
                                              ref={actionDropdownRef}
                                              className={`${tableStyles.actionShapeDropdown}`}
                                              style={{
                                                top: `${dropdownPosition.top}px`,
                                                left: `${dropdownPosition.left}px`,
                                              }}
                                            >
                                              <div>
                                                <Link to={`/dashboard/partner-program/preview-partner/${item._id}`} className={tableStyles.actionShapeDropdownItem}>
                                                  {t('partnerProgram.viewPartner')}
                                                </Link>
                                              </div>
                                              <div>
                                                <Link to={`/dashboard/partner-program/preview-organization/${item.partnerOrganizationId}`} className={tableStyles.actionShapeDropdownItem}>
                                                  {t('partnerProgram.viewPartnerOrganization')}
                                                </Link>
                                              </div>
                                              {/* {!item.permanentlyRemoved && (
                                                <div
                                                  className={tableStyles.actionShapeDropdownItem}
                                                  onClick={() => {
                                                    handleCopyToClipboard(item.referralLink);
                                                  }}
                                                >
                                                  {t('partnerProgram.copyReferralLink')}
                                                </div>
                                              )} */}
                                              {!item.permanentlyRemoved && (
                                                <div className={tableStyles.toggleContainer}>
                                                  <p>{t('partnerProgram.changeStatus')}</p>
                                                  <label className={tableStyles.toggleSwitch}>
                                                    <input type="checkbox" checked={!item.isBlocked} className={tableStyles.toggleInput} onClick={() => handleDeactivateUser(item._id)} />
                                                    <span className={tableStyles.toggleSlider}></span>
                                                  </label>
                                                </div>
                                              )}
                                              <button
                                                className={`${tableStyles.actionShapeDropdownItem} ${tableStyles.redItem}`}
                                                style={{ border: 'none', backgroundColor: 'transparent' }}
                                                onClick={() => {
                                                  handleDeleteUser(item._id);
                                                }}
                                              >
                                                {t('partnerProgram.delete')}
                                              </button>
                                            </div>,
                                            document.body
                                          )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/*table*/}
                    {/*pagination*/}
                    <Pagination onPrevious={() => setCurrentPageAnalytics((prev) => prev - 1)} onNext={() => setCurrentPageAnalytics((prev) => prev + 1)} currentPage={currentPageAnalytics} totalPages={fetchedData?.analytics.pagination.totalPages ?? 1} />
                    {/*pagination*/}
                  </>
                )}
                {/*view analytics*/}
                {/*view links*/}
                {view === 'links' && (
                  <>
                    {/*filters*/}
                    {/*filters*/}
                    <div className={styles.header}>
                      <div className={styles.headerLeft}>
                        <div className={styles.flexRow}>
                          <CustomSearchInput defaultValue={linksSearchTerm} onSearch={setLinksSearchTerm} icon="/assets/images/search-icon.svg" placeholder={t('partnerProgram.search')} />
                        </div>
                      </div>
                    </div>
                    {/*filters*/}
                    {/*filters*/}
                    {/*table*/}
                    <div className={styles.tableBody}>
                      <div className={tableStyles.tableContainer}>
                        <table className={`${tableStyles.table}`}>
                          <thead className={tableStyles.tableHeader}>
                            <tr>
                              <th>{t('partnerProgram.referralLink')}</th>
                              <th>{t('partnerProgram.status')}</th>
                              <th>{t('partnerProgram.dateCreated')}</th>
                              <th className={tableStyles.actionColumn}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fetchedData?.referralLinks?.data?.map((item, index) => {
                              return (
                                <tr key={'referralLink' + item._id}>
                                  <td>{item.referralLink}</td>
                                  <td>{formatStatus(t, item.status)}</td>
                                  <td>{item.dateCreated ?? 'N/A'}</td>
                                  <td className={tableStyles.actionColumn}>
                                    <div className={tableStyles.actionShape} ref={buttonRef}>
                                      <img
                                        src="/assets/images/dots.svg"
                                        alt="dots"
                                        onClick={(e) => {
                                          const buttonElement = e.currentTarget as HTMLDivElement;
                                          handleActionClick('referralLink' + item._id, buttonElement);
                                        }}
                                      />
                                      {isActionOpen === 'referralLink' + item._id &&
                                        typeof window !== 'undefined' &&
                                        createPortal(
                                          <div
                                            ref={actionDropdownRef}
                                            className={`${tableStyles.actionShapeDropdown}`}
                                            style={{
                                              top: `${dropdownPosition.top}px`,
                                              left: `${dropdownPosition.left}px`,
                                            }}
                                          >
                                            <div>
                                              <Link to={`/dashboard/partner-program/preview-partner/${item._id}`} className={tableStyles.actionShapeDropdownItem}>
                                                {t('partnerProgram.viewPartnerDetails')}
                                              </Link>
                                            </div>
                                            <div>
                                              <Link to={`/dashboard/partner-program/preview-organization/${item.partnerOrganizationId}`} className={tableStyles.actionShapeDropdownItem}>
                                                {t('partnerProgram.viewPartnerOrganization')}
                                              </Link>
                                            </div>
                                            <div className={tableStyles.actionShapeDropdownItem} onClick={() => handleDeactivateUser(item._id)}>
                                              {item.isBlocked ? t('partnerProgram.labels.activate') : t('partnerProgram.labels.deactivate')} link
                                            </div>
                                          </div>,
                                          document.body
                                        )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/*table*/}
                    {/*pagination*/}
                    <Pagination onPrevious={() => setCurrentPageLinks((prev) => prev - 1)} onNext={() => setCurrentPageLinks((prev) => prev + 1)} currentPage={currentPageLinks} totalPages={fetchedData?.referralLinks.pagination.totalPages ?? 1} />
                    {/*pagination*/}
                  </>
                )}
                {/*view links*/}
                {/*view earnings*/}
                {view === 'earnings' && (
                  <>
                    {/*header*/}
                    {/*statistics*/}
                    <div className={globalPageStyle.statistics}>
                      {/*TOTAL CLICKS*/}
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('partnerProgram.totalClicks')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{fetchedData?.referralLinkVisits ?? 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      {/*TOTAL CLICKS*/}
                      {/*NUMBER OF SUCCESSFUL CLAIMS*/}
                      {/* <div className={globalPageStyle.statisticsItem}>
                    <p>Number of successful claims</p>
                    <div className={globalPageStyle.statisticsItemBottom}>
                      <div className={globalPageStyle.statisticsItemBottomLeft}>
                        <p className={globalPageStyle.largeP}>58</p>
                      </div>
                    </div>
                  </div> */}
                      {/*NUMBER OF SUCCESSFUL CLAIMS*/}
                      {/*PURCHASES THROUGH THE LINK*/}
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('partnerProgram.purchasesThroughLink')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{fetchedData?.earnings?.pagination?.totalItems ?? 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      {/*PURCHASES THROUGH THE LINK*/}
                    </div>
                    {/*statistics*/}
                    {/*header*/}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/*filters*/}
                      {/*filters*/}
                      <div className={styles.header}>
                        <div className={styles.headerLeft}>
                          <div className={styles.flexRow}>
                            <CustomSearchInput defaultValue={earningsSearchTerm} onSearch={setEarningsSearchTerm} icon="/assets/images/search-icon.svg" placeholder={t('partnerProgram.search')} />
                            {/*filter*/}
                            <CustomDropdown
                              ref={filterStatusDropRef}
                              isOpen={filterStatusOpen}
                              onToggle={() => setFilterStatusOpen(!filterStatusOpen)}
                              options={filterSortByEarningsOptions}
                              value={sortByEarnings}
                              onChange={handleSortByEarningsChange}
                              width="255px"
                              label={t('partnerProgram.sortByEarnings')}
                            />
                            <CustomDropdown
                              ref={filterSortByUserDropRef}
                              isOpen={filterSortByUserOpen}
                              onToggle={() => setFilterSortByUserOpen(!filterSortByUserOpen)}
                              options={filterStatusOptions}
                              value={status}
                              onChange={handleStatusChange}
                              width="255px"
                              label={t('partnerProgram.sortByStatus')}
                            />
                            {/*filter*/}
                          </div>
                        </div>
                      </div>
                      {/*filters*/}
                      {/*filters*/}
                      {/*table*/}
                      <div className={styles.tableBody}>
                        <div className={tableStyles.tableContainer}>
                          <table className={`${tableStyles.table}`}>
                            <thead className={tableStyles.tableHeader}>
                              <tr>
                                <th>{t('partnerProgram.email')}</th>
                                <th>{t('partnerProgram.user')}</th>
                                <th>{t('partnerProgram.rewardAmount')}</th>
                                <th>{t('partnerProgram.status')}</th>
                                <th>{t('partnerProgram.dateAdded')}</th>
                                <th>{t('partnerProgram.expiryDate')}</th>
                                <th className={tableStyles.actionColumn}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {fetchedData?.earnings?.data?.map((item, index) => {
                                return (
                                  <tr key={item.partnerId + 'earnings' + index}>
                                    <td>{item.email}</td>
                                    <td>Partner</td>
                                    <td>€{(item.rewardAmount ?? 0)?.toFixed(2)}</td>
                                    <td>{formatStatus(t, item.status)}</td>
                                    <td>{item.dateAdded}</td>
                                    <td>{item.expiryDate}</td>
                                    <td className={tableStyles.actionColumn}>
                                      <div className={tableStyles.actionShape} ref={buttonRef}>
                                        <img
                                          src="/assets/images/dots.svg"
                                          alt="dots"
                                          onClick={(e) => {
                                            const buttonElement = e.currentTarget as HTMLDivElement;
                                            handleActionClick(item.partnerId + index + 'earnings', buttonElement);
                                          }}
                                        />
                                        {isActionOpen === item.partnerId + index + 'earnings' &&
                                          typeof window !== 'undefined' &&
                                          createPortal(
                                            <div
                                              ref={actionDropdownRef}
                                              className={`${tableStyles.actionShapeDropdown}`}
                                              style={{
                                                top: `${dropdownPosition.top}px`,
                                                left: `${dropdownPosition.left}px`,
                                              }}
                                            >
                                              <div>
                                                <Link to={`/dashboard/partner-program/preview-partner/${item.partnerId}`} className={tableStyles.actionShapeDropdownItem}>
                                                  {t('partnerProgram.viewPartnerDetails')}
                                                </Link>
                                              </div>
                                              <div>
                                                <Link to={`/dashboard/partner-program/preview-organization/${item.partnerOrganizationId}`} className={tableStyles.actionShapeDropdownItem}>
                                                  {t('partnerProgram.viewPartnerOrganization')}
                                                </Link>
                                              </div>
                                              <div className={tableStyles.actionShapeDropdownItem} onClick={() => handleDeactivateUser(item.partnerId)}>
                                                {item.isBlocked ? t('partnerProgram.labels.activate') : t('partnerProgram.labels.deactivate')} link
                                              </div>
                                            </div>,
                                            document.body
                                          )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {/*table*/}
                      {/*pagination*/}
                      <Pagination onPrevious={() => setCurrentPageEarnings(currentPageEarnings - 1)} onNext={() => setCurrentPageEarnings(currentPageEarnings + 1)} currentPage={currentPageEarnings} totalPages={fetchedData?.earnings?.pagination?.totalPages ? fetchedData?.earnings?.pagination?.totalPages : 1} />
                      {/*pagination*/}
                    </div>
                  </>
                )}
                {/*view earnings*/}
              </div>
            </div>
          )
        ) : (
          <h1>{t('partnerProgram.noPermission')}</h1>
        )}
        {/*body - page content*/}
      </div>
    </main>
  );
};
export default PartnerProgram;
